/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useCallback } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Upload, FileSpreadsheet, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import * as XLSX from 'xlsx';

interface FileUploadProps {
  onFileUploaded: (data: {
    headers: string[];
    rows: any[][];
    filename: string;
  }) => void;
}

export function FileUpload({ onFileUploaded }: FileUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parseFile = useCallback(
    async (file: File) => {
      setIsProcessing(true);
      setError(null);

      try {
        const arrayBuffer = await file.arrayBuffer();
        let workbook: XLSX.WorkBook;

        if (file.name.endsWith('.csv')) {
          const text = new TextDecoder().decode(arrayBuffer);
          workbook = XLSX.read(text, { type: 'string' });
        } else {
          workbook = XLSX.read(arrayBuffer, { type: 'array' });
        }

        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
        }) as any[][];

        if (jsonData.length === 0) {
          throw new Error('The file appears to be empty');
        }

        const headers = jsonData[0] as string[];
        const rows = jsonData
          .slice(1)
          .filter((row) =>
            row.some((cell) => cell !== undefined && cell !== ''),
          );

        if (headers.length === 0) {
          throw new Error('No column headers found in the file');
        }

        if (rows.length === 0) {
          throw new Error('No data rows found in the file');
        }

        onFileUploaded({
          headers: headers.map((h) => String(h || '').trim()),
          rows,
          filename: file.name,
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to parse file');
      } finally {
        setIsProcessing(false);
      }
    },
    [onFileUploaded],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);

      const files = Array.from(e.dataTransfer.files);
      const file = files[0];

      if (!file) return;

      const validTypes = ['.csv', '.xls', '.xlsx'];
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();

      if (!validTypes.includes(fileExtension)) {
        setError('Please upload a CSV or Excel file (.csv, .xls, .xlsx)');
        return;
      }

      parseFile(file);
    },
    [parseFile],
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        parseFile(file);
      }
    },
    [parseFile],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-primary" />
            Upload Your Data File
          </CardTitle>
          <CardDescription>
            Upload a CSV or Excel file containing your contact/property data.
            We'll automatically detect and suggest field mappings.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              isDragging
                ? 'border-primary bg-accent'
                : 'border-border hover:border-primary/50 hover:bg-accent/50'
            }`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <div className="mx-auto w-16 h-16 mb-4 rounded-full bg-primary/10 flex items-center justify-center">
              <Upload
                className={`w-8 h-8 ${
                  isDragging ? 'text-primary' : 'text-muted-foreground'
                }`}
              />
            </div>

            <div className="space-y-2">
              <h3 className="text-lg font-medium">
                {isDragging
                  ? 'Drop your file here'
                  : 'Drag and drop your file here'}
              </h3>
              <p className="text-muted-foreground">
                or click to browse for files
              </p>
              <p className="text-sm text-muted-foreground">
                Supports CSV, XLS, and XLSX files
              </p>
            </div>

            <div className="mt-6">
              <Button
                variant="outline"
                disabled={isProcessing}
                onClick={() => document.getElementById('file-input')?.click()}
              >
                {isProcessing ? 'Processing...' : 'Choose File'}
              </Button>
              <input
                id="file-input"
                type="file"
                accept=".csv,.xls,.xlsx"
                onChange={handleFileInput}
                className="hidden"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Required Fields</CardTitle>
          <CardDescription>
            Your file must contain data that can be mapped to these required
            fields:
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {['First Name', 'Last Name', 'Email Address 1'].map((field) => (
              <div
                key={field}
                className="flex items-center gap-2 p-3 rounded-lg bg-muted/50"
              >
                <div className="w-2 h-2 rounded-full bg-destructive"></div>
                <span className="font-medium">{field}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Optional Fields</CardTitle>
          <CardDescription>
            You can also include data for these optional fields. They will be
            mapped if found:
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              'Co-Owner First Name',
              'Co-Owner Last Name',
              'Email Address 2',
              'Telephone',
              'Street Address',
              'Address Line 2',
              'City',
              'State',
              'Zip Code',
            ].map((field) => (
              <div
                key={field}
                className="flex items-center gap-2 p-3 rounded-lg bg-muted/50"
              >
                <div className="w-2 h-2 rounded-full bg-muted-foreground"></div>
                <span className="text-sm">{field}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
